import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

const gradeConfig = {
  'A+': { color: 'from-emerald-500 to-emerald-600', label: 'Crisis Master' },
  'A': { color: 'from-blue-500 to-blue-600', label: 'Crisis Specialist' },
  'B': { color: 'from-amber-500 to-amber-600', label: 'Competent Handler' },
  'C': { color: 'from-red-500 to-red-600', label: 'Needs Practice' },
  'D': { color: 'from-red-800 to-red-900', label: 'Requires Training' },
};

export default function DebriefingSummary({ finalScore, benchmarks }) {
  const [score, setScore] = useState(0);
  const config = gradeConfig[finalScore.grade];

  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const increment = finalScore.total_score / steps;
    const interval = duration / steps;

    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= finalScore.total_score) {
        setScore(finalScore.total_score);
        clearInterval(timer);
      } else {
        setScore(Math.floor(current));
      }
    }, interval);

    return () => clearInterval(timer);
  }, [finalScore.total_score]);

  const expertPos = (benchmarks.expertScore / 100) * 100;
  const averagePos = (benchmarks.averageScore / 100) * 100;
  const yourPos = (finalScore.total_score / 100) * 100;

  return (
    <div className="text-center space-y-6">
      {/* Grade Badge */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
        className={`mx-auto w-32 h-32 rounded-full bg-gradient-to-br ${config.color}
                    flex items-center justify-center shadow-2xl shadow-blue-500/50`}
      >
        <span className="text-6xl font-bold text-white">{finalScore.grade}</span>
      </motion.div>

      {/* Score Display */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <div className="text-6xl font-bold font-mono text-gray-50">
          {score}
          <span className="text-3xl text-gray-500"> / 100</span>
        </div>
        <p className="text-xl text-gray-400 mt-2">{config.label}</p>
      </motion.div>

      {/* Benchmark Comparison */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
        className="mt-8 space-y-4"
      >
        {/* Scale line */}
        <div className="relative h-3 bg-slate-900 rounded-full overflow-visible">
          {/* Expert marker */}
          <div
            className="absolute top-0 h-3 w-0.5 bg-emerald-500"
            style={{ left: `${expertPos}%` }}
          >
            <div className="absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap pointer-events-none">
              <span className="text-xs text-emerald-400 font-medium">Expert</span>
              <span className="block text-xs text-gray-500 mt-0.5">{benchmarks.expertScore}</span>
            </div>
          </div>

          {/* Average marker */}
          <div
            className="absolute top-0 h-3 w-0.5 bg-gray-500"
            style={{ left: `${averagePos}%` }}
          >
            <div className="absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap pointer-events-none">
              <span className="text-xs text-gray-400 font-medium">Average</span>
              <span className="block text-xs text-gray-500 mt-0.5">{benchmarks.averageScore}</span>
            </div>
          </div>

          {/* Your score marker */}
          <div
            className="absolute top-0 h-6 w-6 -mt-1.5 bg-blue-500 rounded-full ring-4 ring-blue-500/30 animate-pulse z-10"
            style={{ left: `calc(${yourPos}% - 12px)` }}
          >
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap pointer-events-none">
              <span className="text-xs text-blue-400 font-semibold">You</span>
              <span className="block text-xs text-gray-50 font-bold mt-0.5">{finalScore.total_score}</span>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
